import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { useCart } from '../context/CartContext';
import { usePublicExperience } from '../context/PublicExperienceContext';
import useSmartScrollVisibility from '../hooks/useSmartScrollVisibility';
import { isInternalLink } from '../utils/publicLinks';

const isCartLink = (link) => String(link?.href || '').trim() === '/carrinho';

function Navbar() {
  const { cartItems } = useCart();
  const { experience } = usePublicExperience();
  const location = useLocation();
  const navbarRef = useRef(null);
  const lastInputWasKeyboardRef = useRef(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [hasKeyboardFocus, setHasKeyboardFocus] = useState(false);
  const isNavbarVisible = useSmartScrollVisibility({
    disabled: isMenuOpen || hasKeyboardFocus,
    resetKey: location.pathname,
  });

  const totalItems = cartItems.reduce((total, item) => total + item.quantity, 0);
  const branding = experience?.data?.branding || {};
  const navigation = experience?.data?.navigation || {};
  const headerLinks = Array.isArray(navigation.header)
    ? navigation.header
        .filter((link) => link?.visible !== false)
        .sort((left, right) => Number(left?.sortOrder || 0) - Number(right?.sortOrder || 0))
    : [];
  const cartLinkFromHeader = headerLinks.find((link) => isCartLink(link));
  const ctaLink = cartLinkFromHeader || navigation.primaryCta || null;
  const regularLinks = headerLinks.filter((link) => !isCartLink(link));

  useEffect(() => {
    function handleClickOutside(event) {
      if (navbarRef.current && !navbarRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    function onKeyDown(event) {
      if (event.key === 'Tab') {
        lastInputWasKeyboardRef.current = true;
      }

      if (event.key === 'Escape') {
        setIsMenuOpen(false);
      }
    }

    function onPointerDown() {
      lastInputWasKeyboardRef.current = false;
      setHasKeyboardFocus(false);
    }

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown, true);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, []);

  const handleFocusCapture = () => {
    if (lastInputWasKeyboardRef.current) {
      setHasKeyboardFocus(true);
    }
  };

  const handleBlurCapture = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setHasKeyboardFocus(false);
    }
  };

  const isActive = (path) => location.pathname === path;

  return (
    <nav
      className={`navbar navbar-expand-lg fixed-top navbar-confeitaria ${
        isNavbarVisible ? 'is-scroll-visible' : 'is-scroll-hidden'
      }`}
      ref={navbarRef}
      onFocusCapture={handleFocusCapture}
      onBlurCapture={handleBlurCapture}
    >
      <div className="container">
        <Link className="navbar-brand" to="/" aria-label="Ir para a página inicial">
          <img
            src={branding?.logo?.src || '/images/logo.png'}
            alt={branding?.logo?.alt || branding?.displayName || 'Logo da marca'}
            className="navbar-logo"
          />
        </Link>

        <button
          className="navbar-toggler"
          type="button"
          aria-controls="navbarNavPremium"
          aria-expanded={isMenuOpen}
          aria-label={isMenuOpen ? 'Fechar menu' : 'Abrir menu'}
          onClick={() => setIsMenuOpen((current) => !current)}
        >
          <svg
            width="26"
            height="26"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M4 7H20M4 12H20M4 17H20"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>

        <div
          id="navbarNavPremium"
          className={`collapse navbar-collapse ${isMenuOpen ? 'show' : ''}`}
        >
          <ul className="navbar-nav ms-auto align-items-lg-center">
            {regularLinks.map((link) => (
              <li className="nav-item" key={link.id || link.href}>
                {isInternalLink(link) ? (
                  <Link
                    className={`nav-link ${isActive(link.href) ? 'active' : ''}`}
                    to={link.href}
                  >
                    {link.label}
                  </Link>
                ) : (
                  <a
                    className="nav-link"
                    href={link.href}
                    target={link.kind === 'external' ? '_blank' : undefined}
                    rel={link.kind === 'external' ? 'noopener noreferrer' : undefined}
                  >
                    {link.label}
                  </a>
                )}
              </li>
            ))}

            {ctaLink && (
              <li className="nav-item ms-lg-3">
                {isInternalLink(ctaLink) ? (
                  <Link className="btn btn-primary btn-carrinho-premium" to={ctaLink.href}>
                    {ctaLink.label}
                    {isCartLink(ctaLink) && (
                      <span className="badge-carrinho" aria-label={`${totalItems} itens no carrinho`}>
                        {totalItems}
                      </span>
                    )}
                  </Link>
                ) : (
                  <a
                    className="btn btn-primary btn-carrinho-premium"
                    href={ctaLink.href}
                    target={ctaLink.kind === 'external' ? '_blank' : undefined}
                    rel={ctaLink.kind === 'external' ? 'noopener noreferrer' : undefined}
                  >
                    {ctaLink.label}
                    {isCartLink(ctaLink) && (
                      <span className="badge-carrinho" aria-label={`${totalItems} itens no carrinho`}>
                        {totalItems}
                      </span>
                    )}
                  </a>
                )}
              </li>
            )}
          </ul>
        </div>
      </div>
    </nav>
  );
}

export default Navbar;
